import { useEffect, useCallback, useRef } from 'react';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../tours/tour-theme.css';

const STORAGE_PREFIX = 'cd365_tour_done_';
const TOURS_DISABLED_KEY = 'cd365_tours_disabled';

export function areToursDisabled(): boolean {
  return localStorage.getItem(TOURS_DISABLED_KEY) === 'true';
}

export function setToursDisabled(disabled: boolean): void {
  if (disabled) {
    localStorage.setItem(TOURS_DISABLED_KEY, 'true');
  } else {
    localStorage.removeItem(TOURS_DISABLED_KEY);
  }
}

export function resetAllTours(): void {
  const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX));
  keys.forEach(k => localStorage.removeItem(k));
}

export function useTour(tourId: string, steps: DriveStep[], options?: {
  autoStart?: boolean;
  delay?: number;
}) {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const autoStart = options?.autoStart ?? true;
  const delay = options?.delay ?? 800;

  const isCompleted = useCallback(() => {
    return localStorage.getItem(`${STORAGE_PREFIX}${tourId}`) === 'true';
  }, [tourId]);

  const markCompleted = useCallback(() => {
    localStorage.setItem(`${STORAGE_PREFIX}${tourId}`, 'true');
  }, [tourId]);

  const resetTour = useCallback(() => {
    localStorage.removeItem(`${STORAGE_PREFIX}${tourId}`);
  }, [tourId]);

  const startTour = useCallback(() => {
    if (driverRef.current) driverRef.current.destroy();

    driverRef.current = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      nextBtnText: 'Weiter',
      prevBtnText: 'Zurück',
      doneBtnText: 'Fertig',
      progressText: '{{current}} von {{total}}',
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      stagePadding: 8,
      stageRadius: 8,
      steps,
      onDestroyed: () => {
        markCompleted();
      },
    });
    driverRef.current.drive();
  }, [steps, markCompleted]);

  useEffect(() => {
    if (!autoStart || isCompleted() || areToursDisabled()) return;

    const timer = setTimeout(() => {
      const firstEl = steps[0]?.element;
      if (firstEl && typeof firstEl === 'string' && !document.querySelector(firstEl)) return;
      startTour();
    }, delay);

    return () => {
      clearTimeout(timer);
      driverRef.current?.destroy();
    };
  }, [autoStart, isCompleted, startTour, delay, steps]);

  useEffect(() => {
    return () => { driverRef.current?.destroy(); };
  }, []);

  return { startTour, resetTour, isCompleted };
}
